import { ContactError } from '@/lib/contacts/contract';
import { syncSignerContact } from '@/lib/contacts/sync-signer';
import { locationsService } from '@/lib/services';
import { applyNonConflictEquipmentSync } from '@/lib/services/invoice-equipment-sync';
import type {
  SlaAgreement,
  SlaAgreementReconciliation,
  SlaAgreementStoreMapping,
  SlaAgreementWritebackContactResult,
  SlaAgreementWritebackLocationResult,
  SlaAgreementWritebackResult,
} from '@/types/sla-agreements';
import type { LocationUpdate } from '@/types/location';

function getMappedStores(reconciliation: SlaAgreementReconciliation): SlaAgreementStoreMapping[] {
  return reconciliation.stores.filter((store) => store.location_id);
}

function addMonthsToDateString(dateString: string, months: number): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const baseDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (Number.isNaN(baseDate.getTime())) {
    return null;
  }

  const nextDate = new Date(baseDate);
  nextDate.setUTCMonth(nextDate.getUTCMonth() + months);
  return nextDate.toISOString().slice(0, 10);
}

async function upsertSignerContact(input: {
  locationId: string;
  signerName: string;
  signerEmail: string | null;
  signerTitle: string | null;
}): Promise<SlaAgreementWritebackContactResult> {
  const result = await syncSignerContact({locationId:input.locationId,name:input.signerName,email:input.signerEmail,role:input.signerTitle});
  return {location_id:input.locationId,...result,action:result.action === 'existing' ? 'skipped' : result.action};
}

export async function applySlaAgreementWriteback(input: {
  agreement: SlaAgreement;
  reconciliation: SlaAgreementReconciliation;
  signedContractPath: string;
}): Promise<SlaAgreementWritebackResult> {
  const { agreement, reconciliation, signedContractPath } = input;
  const mappedStores = getMappedStores(reconciliation);

  if (mappedStores.length !== reconciliation.stores.length) {
    throw new Error('Each reconciled store must be mapped to an existing location before completing the agreement.');
  }

  const locationUpdates: SlaAgreementWritebackLocationResult[] = [];
  const contactUpdates: SlaAgreementWritebackContactResult[] = [];
  const warnings: string[] = [];

  for (const store of mappedStores) {
    const locationId = store.location_id;
    if (!locationId) {
      continue;
    }

    const existingLocation = await locationsService.getById(locationId);
    if (!existingLocation) {
      throw new Error(`Mapped location ${locationId} no longer exists.`);
    }

    const updateData: LocationUpdate = {
      contract_file_path: signedContractPath,
      contract_file_url: null,
      contract_signed_date: reconciliation.execution_date || existingLocation.contract_signed_date,
      contract_term_months: agreement.draft_payload_json.contract_term_months,
      contract_renewal_date: existingLocation.installation_date
        ? addMonthsToDateString(existingLocation.installation_date, agreement.draft_payload_json.contract_term_months) ??
          existingLocation.contract_renewal_date
        : existingLocation.contract_renewal_date,
      contract_signee_name: reconciliation.signer_name || existingLocation.contract_signee_name,
      contract_signee_title: reconciliation.signer_title || existingLocation.contract_signee_title,
      monthly_license_fee:
        agreement.draft_payload_json.software_license_fee_amount ?? existingLocation.monthly_license_fee,
      license_fee_period:
        agreement.draft_payload_json.software_license_fee_period ?? existingLocation.license_fee_period,
      pos_system:
        agreement.reconciliation_json?.pos_system ||
        agreement.draft_payload_json.pos_system ||
        existingLocation.pos_system,
    };

    await locationsService.update(locationId, updateData);

    const appliedFields = Object.entries(updateData)
      .filter(([, value]) => value !== undefined)
      .map(([field]) => field);

    locationUpdates.push({
      location_id: locationId,
      applied_fields: appliedFields,
    });

    let signerResult: SlaAgreementWritebackContactResult;
    try {
      signerResult = await upsertSignerContact({locationId,signerName:reconciliation.signer_name,signerEmail:reconciliation.signer_email,signerTitle:reconciliation.signer_title});
    } catch (error) {
      signerResult = {location_id:locationId,action:'skipped',contact_id:error instanceof ContactError ? error.contactId ?? null : null,note:'Location saved. Contact sync failed. Review location contacts before retrying.'};
    }
    contactUpdates.push(signerResult);
    if (signerResult.note) {
      warnings.push(`Location ${locationId}: ${signerResult.note}`);
    }
  }

  const hardwareCandidates = agreement.draft_payload_json.hardware_schedule_rows
    .filter((row) => row.display_type_id && row.quantity > 0)
    .map((row) => ({
      display_type_id: row.display_type_id as string,
      quantity: row.quantity,
    }));

  let equipment: SlaAgreementWritebackResult['equipment'] = null;

  if (mappedStores.length !== 1) {
    equipment = {
      mode: 'multi_store_skipped',
      order_id: null,
      created_display_type_ids: [],
      skipped_display_type_ids: hardwareCandidates.map((candidate) => candidate.display_type_id),
      note: 'Phase 1 skips equipment writeback when the signed agreement covers multiple mapped stores.',
    };
  } else if (hardwareCandidates.length === 0) {
    equipment = {
      mode: 'single_store_no_candidates',
      order_id: null,
      created_display_type_ids: [],
      skipped_display_type_ids: [],
      note: 'No hardware rows with mapped display types were available for equipment writeback.',
    };
  } else {
    const locationId = mappedStores[0]?.location_id;
    if (!locationId) {
      throw new Error('Single-store equipment writeback requires a mapped location.');
    }

    const result = await applyNonConflictEquipmentSync({
      locationId,
      dealId: agreement.deal_id,
      items: hardwareCandidates,
    });

    equipment = {
      mode: result.skippedOverlaps.length > 0 ? 'single_store_skipped_existing' : 'single_store_applied',
      order_id: result.orderId,
      created_display_type_ids: result.applied.map((candidate) => candidate.display_type_id),
      skipped_display_type_ids: result.skippedOverlaps.map((candidate) => candidate.display_type_id),
      note:
        result.skippedOverlaps.length > 0
          ? 'Existing location equipment with matching display types was kept.'
          : null,
    };
  }

  return {
    location_updates: locationUpdates,
    contact_updates: contactUpdates,
    equipment,
    warnings,
  };
}
